import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Share2 } from "lucide-react";
import { fetchTickerMentions } from "@/lib/api";
import { isInsider } from "@/lib/utils";
import MentionCard from "@/components/MentionCard";
import SignalDots from "@/components/SignalDots";
import InitialsAvatar from "@/components/InitialsAvatar";
import { DisclaimerBanner } from "@/components/Disclaimer";

export default function TickerDetail() {
  const { symbol } = useParams();
  const ticker = (symbol || "").toUpperCase();
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchTickerMentions(ticker);
      setMentions(data);
    } catch {
      setMentions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [ticker]);

  const uniqueExperts = Array.from(
    new Map(mentions.map((m) => [m.expert_id, m])).values()
  );
  const weightedScore = uniqueExperts.reduce(
    (s, m) => s + (isInsider(m.expert_id, ticker) ? 0.4 : 1.0),
    0
  );
  const strength =
    weightedScore >= 4.5
      ? 5
      : weightedScore >= 3.5
      ? 4
      : weightedScore >= 2.5
      ? 3
      : weightedScore >= 1.5
      ? 2
      : 1;
  const company = mentions[0]?.company;

  const handleShare = async () => {
    const url = `${window.location.origin}/ticker/${ticker}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", { description: url });
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div data-testid="ticker-detail-page">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-xs text-[#94a3b8] hover:text-[#f8fafc]"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Convergence feed
      </Link>

      <div className="mt-4 border border-[#1e293b] bg-[#111827] p-4 md:p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-4">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748b]">
              Ticker
            </div>
            <h1 className="mono text-3xl font-bold tracking-tight text-[#f8fafc] md:text-4xl">
              {ticker}
            </h1>
            {company && (
              <div className="mt-1 text-sm text-[#94a3b8]">{company}</div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="share-ticker-btn"
              onClick={handleShare}
              className="inline-flex flex-1 items-center justify-center gap-2 border border-[#1e293b] bg-[#111827] px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#94a3b8] transition-colors hover:border-[#3b82f6] hover:text-[#60a5fa] md:flex-none"
            >
              <Share2 className="h-3.5 w-3.5" /> Share
            </button>
          </div>
        </div>

        {mentions.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-[#1e293b] pt-4 md:gap-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#64748b]">
                Signal Strength
              </div>
              <div className="mt-1">
                <SignalDots strength={strength} />
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#64748b]">
                Weighted Score
              </div>
              <div className="mono text-lg font-bold tabular text-[#f8fafc]">
                {weightedScore.toFixed(1)}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#64748b]">
                Voices
              </div>
              <div className="mono text-lg font-bold tabular">
                {uniqueExperts.length}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#64748b]">
                Total Mentions
              </div>
              <div className="mono text-lg font-bold tabular">
                {mentions.length}
              </div>
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 border-t border-[#1e293b] pt-3 md:w-auto md:border-0 md:pt-0">
              {uniqueExperts.map((m) => {
                const insider = isInsider(m.expert_id, ticker);
                return (
                  <span
                    key={m.expert_id}
                    title={insider ? `${m.expert_name} (insider — reduced weight)` : m.expert_name}
                    className={`inline-flex items-center gap-2 border py-1 pl-1 pr-2.5 ${
                      insider
                        ? "border-[#334155] bg-[#0b1120] opacity-75"
                        : "border-[#1e293b] bg-[#0f172a]"
                    }`}
                  >
                    <InitialsAvatar
                      name={m.expert_name}
                      expertId={m.expert_id}
                      size="sm"
                    />
                    <span className="text-xs font-medium text-[#cbd5e1]">
                      {m.expert_name}
                    </span>
                    {insider && (
                      <span className="font-mono text-[9px] uppercase tracking-wider text-[#f59e0b]">
                        insider
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="mt-4">
        <DisclaimerBanner />
      </div>

      <h2 className="mb-3 mt-6 font-mono text-[10px] uppercase tracking-[0.24em] text-[#64748b]">
        Voices on {ticker} · {mentions.length}
      </h2>

      {loading ? (
        <div className="p-8 text-center text-sm text-[#64748b]">Loading…</div>
      ) : mentions.length === 0 ? (
        <div className="border border-[#1e293b] bg-[#111827] p-8 text-center text-sm text-[#64748b]">
          No tracked voice has mentioned {ticker} in the last 30 days.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {mentions.map((m) => (
            <MentionCard key={m.id} mention={m} hideTicker />
          ))}
        </div>
      )}
    </div>
  );
}
